import {match} from 'ts-pattern';

import {head} from 'ramda';

import {DEFAULT_BALLOON_PAYMENT} from '@dms/features/vehicle-financing-template';

import {FinancingTemplateDraft} from '../types/FinancingTemplateDraft';

/**
 * Fills in the defaults the panel displays for a loaded template, so an unspecified value never
 * reaches the editor as a blank the user has to guess.
 *
 * Optional costs are carried over untouched: a specified zero is a real value the template states,
 * distinct from an unspecified cost. Folding it into `null` would show the cost as blank and, worse,
 * would leave a vehicle-level zero unable to resolve back to "follows the template".
 */
export function normalizeVehicleFinancingTemplateForEditing(
  template: FinancingTemplateDraft
): FinancingTemplateDraft {
  const configuredDefaultCell =
    template.rateMatrix.cells.find(
      (cell) =>
        cell.term === template.defaultTerm &&
        cell.downPayment === template.defaultDownPayment &&
        cell.balloonPayment === template.defaultBalloonPayment
    ) ??
    template.rateMatrix.cells.find(
      (cell) => cell.balloonPayment === template.defaultBalloonPayment
    ) ??
    head(template.rateMatrix.cells);

  return match(template.type)
    .with('STANDARD', () => ({
      ...template,
      interestRateType: template.interestRateType ?? 'FIXED',
      balloonPaymentStep: null,
      defaultTerm: template.defaultTerm ?? configuredDefaultCell?.term ?? null,
      defaultDownPayment: template.defaultDownPayment ?? configuredDefaultCell?.downPayment ?? null,
      defaultBalloonPayment: null,
    }))
    .with('BALLOON', () => ({
      ...template,
      interestRateType: template.interestRateType ?? 'FIXED',
      balloonPaymentStep: template.balloonPaymentStep ?? 'TEN_PERCENT',
      defaultTerm: template.defaultTerm ?? configuredDefaultCell?.term ?? null,
      defaultDownPayment: template.defaultDownPayment ?? configuredDefaultCell?.downPayment ?? null,
      defaultBalloonPayment:
        template.defaultBalloonPayment ??
        configuredDefaultCell?.balloonPayment ??
        DEFAULT_BALLOON_PAYMENT,
    }))
    .exhaustive();
}
